import React, { useCallback, useMemo, useState } from 'react';
import {
  Popover,
  Radio,
  Select,
  Space,
  Typography,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { DuplicateCardsHelpers } from 'ontraccr-common';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import OnTraccrNumberInput from '../../common/inputs/OnTraccrNumberInput';
import OnTraccrRadioGroup from '../../common/inputs/OnTraccrRadioGroup';
import {
  DEFAULT_RELATIVE_DATE,
  RELATIVE_DATE_DIRECTION_OPTIONS,
  RELATIVE_DATE_UNIT_OPTIONS,
  getRelativeDateAnchorLabel,
  getRelativeDateAnchorOptions,
} from '../FormBuilder/FormFields/RelativeDateInput';

const {
  RELATIVE_DATE_ANCHORS,
  getRelativeDateFormulaValue,
  getRelativeDateValueLabel,
  isRelativeDateComplete,
} = DuplicateCardsHelpers;

const { Text } = Typography;

const MODES = { SAME_DAY: 'sameDay', RELATIVE: 'relative' };
const MODE_OPTIONS = [
  { value: MODES.SAME_DAY, label: 'Same day' },
  { value: MODES.RELATIVE, label: 'Relative date' },
];

const OPERATOR_OPTIONS = [
  { value: 'isBefore', label: 'Before' },
  { value: 'isAfter', label: 'After' },
  { value: 'within', label: 'Within' },
];
const OPERATOR_LABELS = Object.fromEntries(
  OPERATOR_OPTIONS.map(({ value, label }) => [value, label]),
);

const DEFAULT_RULE = { operator: 'within', ...DEFAULT_RELATIVE_DATE };

// e.g. "6 months before Today"
const getRuleOffsetText = (rule, sourceLabel) => {
  const { amount, unit = '', direction } = rule;
  const unitLabel = amount === 1 ? unit.replace(/s$/, '') : unit;
  return `${amount} ${unitLabel} ${direction} ${getRelativeDateAnchorLabel(rule.anchor, sourceLabel)}`;
};

const getRuleSummary = (rule, sourceLabel) => (
  `${OPERATOR_LABELS[rule.operator]} ${getRuleOffsetText(rule, sourceLabel)}`
);

const getPopupContainer = (trigger) => trigger.parentNode;

export default function RelativeDateRuleEditor({
  cardFieldLabel,
  sourceLabel,
  value,
  disabled,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(MODES.SAME_DAY);
  const [draft, setDraft] = useState(DEFAULT_RULE);

  const openEditor = useCallback((nextMode) => {
    setMode(nextMode);
    setDraft(value ?? DEFAULT_RULE);
    setOpen(true);
  }, [value]);

  const onModeSelect = useCallback((nextMode) => {
    if (nextMode === MODES.SAME_DAY) {
      onChange(undefined);
      return;
    }
    openEditor(MODES.RELATIVE);
  }, [onChange, openEditor]);

  const onApply = useCallback(() => {
    onChange(mode === MODES.RELATIVE ? draft : undefined);
    setOpen(false);
  }, [onChange, mode, draft]);

  const onDraftChange = useCallback((key) => (newPart) => {
    setDraft((prev) => ({ ...prev, [key]: newPart }));
  }, []);

  const anchorOptions = useMemo(() => getRelativeDateAnchorOptions({
    fieldLabel: sourceLabel,
    hasFieldAnchor: true,
  }), [sourceLabel]);

  const isDraftComplete = isRelativeDateComplete(draft);

  const preview = useMemo(() => {
    if (mode !== MODES.RELATIVE || !isDraftComplete) return null;
    if (draft.anchor === RELATIVE_DATE_ANCHORS.TODAY) {
      const label = getRelativeDateValueLabel({
        operator: draft.operator,
        value: getRelativeDateFormulaValue({ operator: draft.operator, relativeDate: draft }),
      });
      return `Today this matches cards whose ${cardFieldLabel} is ${label}.`;
    }
    const operatorLabel = OPERATOR_LABELS[draft.operator].toLowerCase();
    return `Matches cards whose ${cardFieldLabel} is ${operatorLabel} ${getRuleOffsetText(draft, sourceLabel)}.`;
  }, [mode, isDraftComplete, draft, cardFieldLabel, sourceLabel]);

  const isRelative = mode === MODES.RELATIVE;

  const content = (
    <Space direction="vertical" size={10} style={{ width: 380 }}>
      <Text strong>{`Date match - ${cardFieldLabel}`}</Text>
      <OnTraccrRadioGroup value={mode} onChange={setMode}>
        <Space direction="vertical" size={6}>
          <Radio value={MODES.SAME_DAY}>{`Same day as ${sourceLabel} on this form`}</Radio>
          <Radio value={MODES.RELATIVE}>Relative date</Radio>
        </Space>
      </OnTraccrRadioGroup>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr)',
          gap: '8px 10px',
          alignItems: 'center',
          paddingLeft: 24,
        }}
      >
        <Text type="secondary">Card date is</Text>
        <Select
          disabled={!isRelative}
          options={OPERATOR_OPTIONS}
          value={draft.operator}
          onChange={onDraftChange('operator')}
          getPopupContainer={getPopupContainer}
        />
        <Text type="secondary">Offset</Text>
        <Space size={6}>
          <OnTraccrNumberInput
            disabled={!isRelative}
            min={1}
            precision={0}
            style={{ width: 64, display: 'block' }}
            value={draft.amount}
            onChange={onDraftChange('amount')}
          />
          <Select
            disabled={!isRelative}
            style={{ width: 100 }}
            options={RELATIVE_DATE_UNIT_OPTIONS}
            value={draft.unit}
            onChange={onDraftChange('unit')}
            getPopupContainer={getPopupContainer}
          />
          <Select
            disabled={!isRelative}
            style={{ width: 90 }}
            options={RELATIVE_DATE_DIRECTION_OPTIONS}
            value={draft.direction}
            onChange={onDraftChange('direction')}
            getPopupContainer={getPopupContainer}
          />
        </Space>
        <Text type="secondary">Anchor</Text>
        <Select
          disabled={!isRelative}
          dropdownMatchSelectWidth={false}
          options={anchorOptions}
          value={draft.anchor}
          onChange={onDraftChange('anchor')}
          getPopupContainer={getPopupContainer}
        />
      </div>
      {preview && (
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
          <Text type="secondary">{preview}</Text>
        </div>
      )}
      {isRelative && !isDraftComplete && (
        <Text type="danger">Enter a whole number of at least 1</Text>
      )}
      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <OnTraccrButton title="Cancel" type="cancel" onClick={() => setOpen(false)} />
        <OnTraccrButton
          title="Apply"
          onClick={onApply}
          disabled={isRelative && !isDraftComplete}
        />
      </Space>
    </Space>
  );

  const summary = value ? getRuleSummary(value, sourceLabel) : null;

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      visible={open}
      onVisibleChange={(visible) => { if (!visible) setOpen(false); }}
      content={content}
      destroyTooltipOnHide
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Select
          style={{ width: '100%' }}
          disabled={disabled}
          options={MODE_OPTIONS}
          value={value || (open && isRelative) ? MODES.RELATIVE : MODES.SAME_DAY}
          onSelect={onModeSelect}
        />
        {summary && (
          <div style={{ fontSize: 12, lineHeight: '18px' }}>
            <Text type="secondary" disabled={disabled} style={{ fontSize: 12 }}>
              {summary}
            </Text>
            {!disabled && (
              <EditOutlined
                style={{ marginLeft: 6, cursor: 'pointer', color: '#8c8c8c' }}
                onClick={() => openEditor(MODES.RELATIVE)}
              />
            )}
          </div>
        )}
      </div>
    </Popover>
  );
}

RelativeDateRuleEditor.propTypes = {
  cardFieldLabel: PropTypes.string,
  sourceLabel: PropTypes.string,
  value: PropTypes.shape({
    operator: PropTypes.string,
    amount: PropTypes.number,
    unit: PropTypes.string,
    direction: PropTypes.string,
    anchor: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

RelativeDateRuleEditor.defaultProps = {
  cardFieldLabel: '',
  sourceLabel: '',
  value: undefined,
  disabled: false,
};
